import { createContext, useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import { API_BASE } from "../config/api";
import {
  getReturnPath,
  savePendingAuthRedirect,
} from "../utils/authRedirect";

export const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const cartRef = useRef([]);

  const normalizeSelectedSize = (sizeValue = '') => String(sizeValue || '').trim();
  const getProductStockQuantity = (product = {}) => {
    const parsedStockQuantity = Number(product?.stockQuantity);
    return Number.isFinite(parsedStockQuantity) ? Math.max(0, Math.floor(parsedStockQuantity)) : 0;
  };
  const getProductCartQuantity = (items = [], productId = '') =>
    items.reduce((total, item) => (
      item.product?._id === productId ? total + (Number(item.quantity) || 0) : total
    ), 0);

  // Helper to get user-specific key
  const getCartKey = () => user ? `cart_${user._id || user.email}` : 'cart_guest';

  // Load cart from localStorage when user changes
  useEffect(() => {
    if (user) {
      const savedCart = localStorage.getItem(getCartKey());
      if (savedCart) {
        setCart(JSON.parse(savedCart));
      } else {
        setCart([]);
      }
    } else {
      setCart([]);
    }
    setLoading(false);
    // eslint-disable-next-line
  }, [user]);

  // Save cart to localStorage whenever it changes and user is present
  useEffect(() => {
    if (!loading && user) {
      localStorage.setItem(getCartKey(), JSON.stringify(cart));
    }
    // eslint-disable-next-line
  }, [cart, loading, user]);

  useEffect(() => {
    cartRef.current = cart;
  }, [cart]);

  const hasProductSnapshotChanged = (currentProduct = {}, nextProduct = {}) => (
    currentProduct?.title !== nextProduct?.title
    || Number(currentProduct?.price) !== Number(nextProduct?.price)
    || Number(currentProduct?.offerPrice) !== Number(nextProduct?.offerPrice)
    || Number(currentProduct?.stockQuantity) !== Number(nextProduct?.stockQuantity)
    || currentProduct?.material !== nextProduct?.material
    || currentProduct?.description !== nextProduct?.description
    || JSON.stringify(currentProduct?.images || []) !== JSON.stringify(nextProduct?.images || [])
    || JSON.stringify(currentProduct?.availableSizes || []) !== JSON.stringify(nextProduct?.availableSizes || [])
    || JSON.stringify(currentProduct?.shippingDetails || {}) !== JSON.stringify(nextProduct?.shippingDetails || {})
  );

  const syncCartProducts = useCallback(async () => {
    if (!user || cartRef.current.length === 0) {
      return cartRef.current;
    }

    const productIds = [...new Set(
      cartRef.current
        .map((item) => item.product?._id)
        .filter(Boolean)
    )];

    if (productIds.length === 0) {
      return cartRef.current;
    }

    const productEntries = await Promise.all(
      productIds.map(async (productId) => {
        try {
          const response = await axios.get(`${API_BASE}/products/${productId}`);
          return [productId, response.data];
        } catch (error) {
          return [productId, null];
        }
      })
    );

    const latestProductMap = new Map(productEntries);
    let syncedCartSnapshot = cartRef.current;

    setCart((currentCart) => {
      let didChange = false;

      const nextCart = currentCart.map((item) => {
        const latestProduct = latestProductMap.get(item.product?._id);

        if (!latestProduct) {
          return item;
        }

        const nextProduct = {
          ...item.product,
          ...latestProduct,
        };

        if (!hasProductSnapshotChanged(item.product, nextProduct)) {
          return item;
        }

        didChange = true;
        return {
          ...item,
          product: nextProduct,
        };
      });

      syncedCartSnapshot = didChange ? nextCart : currentCart;
      return didChange ? nextCart : currentCart;
    });

    return syncedCartSnapshot;
  }, [user]);

  // Add item to cart
  const addToCart = (product, quantity = 1, selectedSize = '') => {
    if (!user) {
      const returnPath = getReturnPath(location);
      savePendingAuthRedirect(returnPath);
      navigate(
        "/login?message=" + encodeURIComponent("Please sign in or register to add items to your cart."),
        { state: { from: returnPath } }
      );
      return false;
    }

    const normalizedSelectedSize = normalizeSelectedSize(selectedSize);
    const stockQuantity = getProductStockQuantity(product);
    const requestedQuantity = Math.max(1, Number(quantity) || 1);
    const existingProductQuantity = getProductCartQuantity(cart, product._id);

    if (stockQuantity <= 0) {
      window.alert("This product is currently out of stock.");
      return false;
    }

    if (existingProductQuantity + requestedQuantity > stockQuantity) {
      window.alert(`Only ${stockQuantity} item(s) are available in stock for this product.`);
      return false;
    }

    setCart((prevCart) => {
      // Check if product already exists in cart
      const existingItemIndex = prevCart.findIndex(
        (item) =>
          item.product._id === product._id &&
          normalizeSelectedSize(item.selectedSize) === normalizedSelectedSize
      );

      if (existingItemIndex !== -1) {
        // Update quantity if product already exists
        const updatedCart = [...prevCart];
        updatedCart[existingItemIndex].quantity += requestedQuantity;
        return updatedCart;
      } else {
        // Add new product to cart
        return [...prevCart, { product, quantity: requestedQuantity, selectedSize: normalizedSelectedSize }];
      }
    });
    return true;
  };

  // Remove item from cart
  const removeFromCart = (productId, selectedSize = '') => {
    const normalizedSelectedSize = normalizeSelectedSize(selectedSize);
    setCart((prevCart) =>
      prevCart.filter(
        (item) =>
          !(
            item.product._id === productId &&
            normalizeSelectedSize(item.selectedSize) === normalizedSelectedSize
          )
      )
    );
  };

  // Update item quantity
  const updateQuantity = (productId, quantity, selectedSize = '') => {
    const normalizedSelectedSize = normalizeSelectedSize(selectedSize);
    const parsedQuantity = parseInt(quantity, 10);

    if (parsedQuantity <= 0) {
      removeFromCart(productId, normalizedSelectedSize);
      return;
    }

    setCart((prevCart) => {
      const targetItem = prevCart.find(
        (item) =>
          item.product._id === productId &&
          normalizeSelectedSize(item.selectedSize) === normalizedSelectedSize
      );

      if (!targetItem) {
        return prevCart;
      }

      const stockQuantity = getProductStockQuantity(targetItem.product);
      const otherProductQuantity = prevCart.reduce((total, item) => {
        if (item.product?._id !== productId) {
          return total;
        }

        if (normalizeSelectedSize(item.selectedSize) === normalizedSelectedSize) {
          return total;
        }

        return total + (Number(item.quantity) || 0);
      }, 0);

      const nextQuantity = Math.max(1, parsedQuantity);
      const cappedQuantity = Math.min(nextQuantity, Math.max(0, stockQuantity - otherProductQuantity));

      if (cappedQuantity <= 0) {
        window.alert("No more stock is available for this product.");
        return prevCart;
      }

      if (cappedQuantity !== nextQuantity) {
        window.alert(`Only ${stockQuantity} item(s) are available in stock for this product.`);
      }

      return prevCart.map((item) =>
        item.product._id === productId &&
        normalizeSelectedSize(item.selectedSize) === normalizedSelectedSize
          ? { ...item, quantity: cappedQuantity }
          : item
      );
    });
  };

  // Clear cart
  const clearCart = () => {
    setCart([]);
  };

  // Calculate total price
  const getTotalPrice = () => {
    return cart.reduce(
      (total, item) => total + item.product.offerPrice * item.quantity,
      0
    );
  };

  // Get total number of items in cart
  const getCartCount = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  // Check if product is in cart
  const isInCart = (productId, selectedSize = null) => {
    if (selectedSize === null) {
      return cart.some((item) => item.product._id === productId);
    }

    const normalizedSelectedSize = normalizeSelectedSize(selectedSize);
    return cart.some(
      (item) =>
        item.product._id === productId &&
        normalizeSelectedSize(item.selectedSize) === normalizedSelectedSize
    );
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        loading,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getTotalPrice,
        getCartCount,
        isInCart,
        syncCartProducts,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
